/**
 * P439.3 -- importing the bank's architecture model, reviewed.
 *
 * Upload an ArchiMate exchange file; see what each element matched and
 * how; confirm the name matches a person must confirm; accept or reject
 * the edges the model would change, singly or a whole kind at once;
 * commit. Nothing is written until the commit, and removing an edge the
 * model no longer holds is off unless asked for, and only ever touches
 * an edge an earlier import wrote.
 */
import { refusalMessage } from "shared/api/refusalMessage";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api, currentAuthToken, type ApiError } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { useToast } from "shared/toast/ToastProvider";

const URL = "/v1/admin/foundation/ea-import";
const KEY = ["ea-import"] as const;
const P = "modules.foundation.eaImport";

export interface EaRun {
  id: string;
  filename: string;
  file_sha256: string;
  model_name: string;
  status: "staged" | "committed" | "discarded";
  summary: {
    elements?: number;
    relationships?: number;
    unmapped_element_types?: Record<string, number>;
    unmapped_relationships?: Record<string, number>;
    dangling?: number;
    matches?: Record<string, number>;
    edges?: Record<string, number>;
  };
  created_at: string;
  committed_at: string | null;
  discarded_at: string | null;
}

export interface EaElement {
  ea_id: string;
  ea_type: string;
  ea_name: string;
  kind: string | null;
  entity_id: string | null;
  how: string;
  settled: boolean;
  candidates: number;
  decision: string;
}

export interface EaEdge {
  id: string;
  ea_relationship_id: string | null;
  relationship_type: string;
  kind: string | null;
  left_ea_id: string | null;
  right_ea_id: string | null;
  left_entity_id: string | null;
  right_entity_id: string | null;
  left_name: string | null;
  right_name: string | null;
  status: string;
  needs_identity: boolean;
  decision: string;
}

export interface EaRunDetail extends EaRun {
  elements: EaElement[];
  edges: EaEdge[];
}

class UploadError extends Error {}

async function upload(file: File): Promise<EaRunDetail> {
  const token = currentAuthToken();
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(URL, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const detail =
      body && typeof (body as { detail?: unknown }).detail === "string"
        ? (body as { detail: string }).detail
        : res.statusText;
    throw new UploadError(detail);
  }
  return body as EaRunDetail;
}

/** The kinds in the order a reader meets them on the capability map. */
const EDGE_KINDS = [
  "capability_parent",
  "capability_dependency",
  "capability_ibs",
  "ibs_service",
] as const;

const DECIDABLE = new Set(["new", "absent", "rejected_before"]);

function Pill({
  tone,
  children,
}: {
  tone: "ok" | "wait" | "no" | "plain";
  children: string;
}) {
  const cls = {
    ok: "bg-emerald-50 text-emerald-800",
    wait: "bg-amber-50 text-amber-800",
    no: "bg-rose-50 text-rose-800",
    plain: "bg-slate-100 text-slate-700",
  }[tone];
  return <span className={`rounded-full px-2 py-0.5 text-xs ${cls}`}>{children}</span>;
}

function Identities({ run }: { run: EaRunDetail }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const decide = useMutation<EaElement, ApiError, { ea_id: string; decision: string }>({
    mutationFn: ({ ea_id, decision }) =>
      api.patch<EaElement>(`${URL}/${run.id}/elements/${encodeURIComponent(ea_id)}`, {
        decision,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, run.id] }),
    onError: (err) => toast.push({ tone: "error", message: refusalMessage(t, err) }),
  });
  const open = run.status === "staged";
  const toConfirm = run.elements.filter(
    (e) => e.how === "name" || e.how === "ambiguous",
  );
  const unmatched = run.elements.filter((e) => e.how === "unmatched");
  const settled = run.elements.filter((e) => e.settled && e.how !== "unmapped_type");
  const unmapped = run.elements.filter((e) => e.how === "unmapped_type");
  return (
    <section data-testid="ea-identities" className="space-y-2">
      <h2 className="text-sm font-semibold text-slate-900">
        {t(`${P}.identities.heading`)}
      </h2>
      <p className="text-xs text-slate-600">
        {t(`${P}.identities.settled`, { count: settled.length })}{" "}
        {unmapped.length > 0 &&
          t(`${P}.identities.unmapped`, { count: unmapped.length })}
      </p>
      {toConfirm.length > 0 && (
        <ul className="space-y-1">
          {toConfirm.map((e) => (
            <li
              key={e.ea_id}
              data-testid="ea-identity-row"
              data-decision={e.decision}
              className="flex flex-wrap items-center gap-2 rounded border border-slate-200 bg-white p-2 text-sm"
            >
              <span className="font-medium text-slate-900">{e.ea_name}</span>
              <Pill tone="plain">{t(`${P}.kind.${e.kind ?? "none"}`)}</Pill>
              <span className="text-xs text-slate-600">
                {e.how === "ambiguous"
                  ? t(`${P}.identities.ambiguous`, { count: e.candidates })
                  : t(`${P}.identities.byName`)}
              </span>
              {e.decision === "confirmed" && (
                <Pill tone="ok">{t(`${P}.decision.confirmed`)}</Pill>
              )}
              {e.decision === "rejected" && (
                <Pill tone="no">{t(`${P}.decision.rejected`)}</Pill>
              )}
              {open && e.how === "name" && e.decision === "pending" && (
                <span className="ms-auto flex gap-2">
                  <button
                    type="button"
                    data-testid="ea-confirm"
                    onClick={() =>
                      decide.mutate({ ea_id: e.ea_id, decision: "confirmed" })
                    }
                    className="rounded bg-brand-brass px-2 py-0.5 text-xs font-medium text-brand-navy hover:bg-brand-brassDark"
                  >
                    {t(`${P}.confirm`)}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      decide.mutate({ ea_id: e.ea_id, decision: "rejected" })
                    }
                    className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-700"
                  >
                    {t(`${P}.notThis`)}
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {unmatched.length > 0 && (
        <ul className="space-y-1">
          {unmatched.map((e) => (
            <li
              key={e.ea_id}
              data-testid="ea-unmatched-row"
              className="flex flex-wrap items-center gap-2 rounded border border-slate-200 bg-white p-2 text-sm"
            >
              <span className="font-medium text-slate-900">{e.ea_name}</span>
              <Pill tone="plain">{t(`${P}.kind.${e.kind ?? "none"}`)}</Pill>
              <span className="text-xs text-slate-600">
                {e.kind === "capability"
                  ? t(`${P}.identities.canCreate`)
                  : e.kind === "ibs_service"
                    ? t(`${P}.identities.raiseIbs`)
                    : t(`${P}.identities.fromCmdb`)}
              </span>
              {e.decision === "create" && (
                <Pill tone="ok">
                  {t(
                    run.status === "committed"
                      ? `${P}.decision.created`
                      : `${P}.decision.create`,
                  )}
                </Pill>
              )}
              {open && e.kind === "capability" && e.decision === "pending" && (
                <button
                  type="button"
                  data-testid="ea-create"
                  onClick={() => decide.mutate({ ea_id: e.ea_id, decision: "create" })}
                  className="ms-auto rounded bg-brand-brass px-2 py-0.5 text-xs font-medium text-brand-navy hover:bg-brand-brassDark"
                >
                  {t(`${P}.create`)}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function statusTone(status: string): "ok" | "wait" | "no" | "plain" {
  if (status === "present") return "ok";
  if (status === "new") return "wait";
  if (status === "absent" || status === "rejected_before") return "no";
  return "plain";
}

function Edges({ run }: { run: EaRunDetail }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [showPresent, setShowPresent] = useState(false);
  const decide = useMutation<
    { decided: number },
    ApiError,
    { ids: string[]; decision: string }
  >({
    mutationFn: ({ ids, decision }) =>
      api.patch<{ decided: number }>(`${URL}/${run.id}/edges`, {
        suggestion_ids: ids,
        decision,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, run.id] }),
    onError: (err) => toast.push({ tone: "error", message: refusalMessage(t, err) }),
  });
  const open = run.status === "staged";
  /* The staged flag is a snapshot; say "waits" only while an end is
     still an unconfirmed match in this run, and never once committed. */
  const byId = new Map(run.elements.map((el) => [el.ea_id, el]));
  const resolved = (id: string | null) => {
    const el = id ? byId.get(id) : undefined;
    return !el || el.settled || el.decision === "confirmed" || el.decision === "create";
  };
  const waits = (e: EaEdge) =>
    open && e.needs_identity && !(resolved(e.left_ea_id) && resolved(e.right_ea_id));
  const present = run.edges.filter((e) => e.status === "present").length;
  const other = run.edges.filter((e) => e.kind === null);
  return (
    <section data-testid="ea-edges" className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold text-slate-900">
          {t(`${P}.edges.heading`)}
        </h2>
        <span className="text-xs text-slate-600">
          {t(`${P}.edges.present`, { count: present })}
        </span>
        <label className="ms-auto flex items-center gap-1 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={showPresent}
            onChange={(e) => setShowPresent(e.target.checked)}
          />
          {t(`${P}.edges.showPresent`)}
        </label>
      </div>
      {EDGE_KINDS.map((kind) => {
        const rows = run.edges.filter(
          (e) => e.kind === kind && (showPresent || e.status !== "present"),
        );
        if (rows.length === 0) return null;
        const pending = rows.filter(
          (e) => DECIDABLE.has(e.status) && e.decision === "pending",
        );
        return (
          <div key={kind} data-testid={`ea-edges-${kind}`} className="space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                {t(`${P}.edgeKind.${kind}`)}
              </h3>
              {open && pending.length > 1 && (
                <button
                  type="button"
                  data-testid={`ea-accept-all-${kind}`}
                  onClick={() =>
                    decide.mutate({
                      ids: pending.map((e) => e.id),
                      decision: "accepted",
                    })
                  }
                  className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-xs text-brand-brassDark"
                >
                  {t(`${P}.acceptAll`, { count: pending.length })}
                </button>
              )}
            </div>
            <ul className="space-y-1">
              {rows.map((e) => (
                <li
                  key={e.id}
                  data-testid="ea-edge-row"
                  data-status={e.status}
                  data-decision={e.decision}
                  className="flex flex-wrap items-center gap-2 rounded border border-slate-200 bg-white p-2 text-sm"
                >
                  <span className="text-slate-900">
                    {t(`${P}.edgeSentence.${kind}`, {
                      left: e.left_name ?? "?",
                      right: e.right_name ?? "?",
                    })}
                  </span>
                  <Pill tone={statusTone(e.status)}>
                    {t(`${P}.status.${e.status}`)}
                  </Pill>
                  {waits(e) && (
                    <span
                      data-testid="ea-edge-waits"
                      className="text-xs text-amber-800"
                    >
                      {t(`${P}.edges.needsIdentity`)}
                    </span>
                  )}
                  {e.decision !== "pending" && (
                    <Pill tone={e.decision === "accepted" ? "ok" : "no"}>
                      {t(`${P}.decision.${e.decision}`)}
                    </Pill>
                  )}
                  {open && DECIDABLE.has(e.status) && (
                    <span className="ms-auto flex gap-2">
                      <button
                        type="button"
                        data-testid="ea-accept"
                        onClick={() =>
                          decide.mutate({ ids: [e.id], decision: "accepted" })
                        }
                        className="rounded bg-brand-brass px-2 py-0.5 text-xs font-medium text-brand-navy hover:bg-brand-brassDark"
                      >
                        {e.status === "absent"
                          ? t(`${P}.markRemove`)
                          : t(`${P}.accept`)}
                      </button>
                      <button
                        type="button"
                        data-testid="ea-reject"
                        onClick={() =>
                          decide.mutate({ ids: [e.id], decision: "rejected" })
                        }
                        className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-700"
                      >
                        {e.status === "absent" ? t(`${P}.keep`) : t(`${P}.reject`)}
                      </button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      {other.length > 0 && (
        <p data-testid="ea-cmdb-owned" className="text-xs text-slate-600">
          {t(`${P}.edges.cmdbOwned`, { count: other.length })}
        </p>
      )}
    </section>
  );
}

function Commit({ run }: { run: EaRunDetail }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [removeAbsent, setRemoveAbsent] = useState(false);
  const accepted = run.edges.filter(
    (e) => e.decision === "accepted" && e.status !== "absent",
  );
  const toRemove = run.edges.filter(
    (e) => e.decision === "accepted" && e.status === "absent",
  );
  const creating = run.elements.filter((e) => e.decision === "create");
  const done = () => void qc.invalidateQueries({ queryKey: KEY });
  const commit = useMutation<{ result: Record<string, number> }, ApiError>({
    mutationFn: () =>
      api.post<{ result: Record<string, number> }>(`${URL}/${run.id}/commit`, {
        remove_absent: removeAbsent,
      }),
    onSuccess: (out) => {
      toast.push({
        tone: "success",
        message: t(`${P}.committed`, {
          written: (out.result.edges_written ?? 0) + (out.result.parents_set ?? 0),
          waiting: out.result.waiting_on_identity ?? 0,
        }),
      });
      done();
    },
    onError: (err) => toast.push({ tone: "error", message: refusalMessage(t, err) }),
  });
  const discard = useMutation<EaRun, ApiError>({
    mutationFn: () => api.post<EaRun>(`${URL}/${run.id}/discard`),
    onSuccess: done,
    onError: (err) => toast.push({ tone: "error", message: refusalMessage(t, err) }),
  });
  if (run.status !== "staged") {
    return (
      <p data-testid="ea-run-closed" className="text-sm text-slate-600">
        {t(`${P}.closed.${run.status}`)}
      </p>
    );
  }
  return (
    <section
      data-testid="ea-commit"
      className="space-y-2 rounded border border-slate-200 bg-white p-3 text-sm"
    >
      <h2 className="text-sm font-semibold text-slate-900">
        {t(`${P}.preview.heading`)}
      </h2>
      <p data-testid="ea-preview" className="text-xs text-slate-700">
        {t(`${P}.preview.write`, { count: accepted.length })}{" "}
        {creating.length > 0 && t(`${P}.preview.create`, { count: creating.length })}
      </p>
      {toRemove.length > 0 && (
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            data-testid="ea-remove-absent"
            checked={removeAbsent}
            onChange={(e) => setRemoveAbsent(e.target.checked)}
          />
          {t(`${P}.preview.remove`, { count: toRemove.length })}
        </label>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="ea-commit-button"
          disabled={commit.isPending}
          onClick={() => commit.mutate()}
          className="rounded bg-brand-brass px-3 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {t(`${P}.commit`)}
        </button>
        <button
          type="button"
          data-testid="ea-discard"
          disabled={discard.isPending}
          onClick={() => discard.mutate()}
          className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-700"
        >
          {t(`${P}.discard`)}
        </button>
      </div>
    </section>
  );
}

export function EaImportAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<string | null>(null);
  const runs = useQuery<EaRun[], ApiError>({
    queryKey: KEY,
    queryFn: () => api.get<EaRun[]>(URL),
  });
  const list = useMemo(() => (Array.isArray(runs.data) ? runs.data : []), [runs.data]);
  const current = selected ?? list.find((r) => r.status === "staged")?.id ?? null;
  const detail = useQuery<EaRunDetail, ApiError>({
    queryKey: [...KEY, current],
    queryFn: () => api.get<EaRunDetail>(`${URL}/${current}`),
    enabled: current !== null,
  });
  const up = useMutation<EaRunDetail, UploadError, File>({
    meta: { handled: true },
    mutationFn: upload,
    onSuccess: (run) => {
      setSelected(run.id);
      void qc.invalidateQueries({ queryKey: KEY });
    },
    onError: (err) => toast.push({ tone: "error", message: err.message }),
  });
  const run =
    detail.data &&
    Array.isArray(detail.data.elements) &&
    Array.isArray(detail.data.edges)
      ? detail.data
      : null;
  return (
    <article className="space-y-4" data-testid="ea-import-page">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 font-display text-2xl text-slate-900">
          <MarsadIcon name="capability-map" className="text-brand-brass" />
          {t(`${P}.heading`)}
        </h1>
        <p className="text-sm text-slate-600">{t(`${P}.standfirst`)}</p>
      </header>
      <label className="block rounded border border-dashed border-slate-300 bg-white p-3 text-sm text-slate-700">
        {t(`${P}.upload`)}
        <input
          type="file"
          accept=".xml,application/xml,text/xml"
          data-testid="ea-upload"
          disabled={up.isPending}
          className="mt-1 block text-xs"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) up.mutate(file);
            e.target.value = "";
          }}
        />
      </label>
      {list.length > 0 && (
        <ul data-testid="ea-runs" className="flex flex-wrap gap-2 text-xs">
          {list.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => setSelected(r.id)}
                aria-pressed={r.id === current}
                className={[
                  "rounded border px-2 py-0.5",
                  r.id === current
                    ? "border-brand-brass bg-brand-brass/10 text-brand-navy"
                    : "border-slate-300 text-slate-700",
                ].join(" ")}
              >
                {r.filename} · {t(`${P}.runStatus.${r.status}`)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {runs.isSuccess && list.length === 0 && (
        <p data-testid="ea-empty" className="text-sm text-slate-600">
          {t(`${P}.empty`)}
        </p>
      )}
      {run && (
        <div className="space-y-4">
          <p data-testid="ea-summary" className="text-xs text-slate-600">
            {t(`${P}.summary`, {
              model: run.model_name || run.filename,
              elements: run.summary.elements ?? 0,
              relationships: run.summary.relationships ?? 0,
              sha: run.file_sha256.slice(0, 12),
            })}
          </p>
          <Identities run={run} />
          <Edges run={run} />
          <Commit run={run} />
        </div>
      )}
    </article>
  );
}
